import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { ConfigurationSection } from '@features/settings/pages/StyleComponents';
import { BodyLarge, BodyRegular, BodySmall } from '../../atoms/fonts/Body';
import Colours from '../../atoms/Colours';
import { SimpleSpinner } from '../../atoms/SimpleSpinner';
import { PrimaryButton } from '../../atoms/buttons/PrimaryButton';
import { OutlineButton } from '../../atoms/buttons/OutlineButton';
import { ButtonSize } from '../../atoms/buttons/ButtonStyling';
import { selectCurrentUser } from '@shared/store/auth.duck';
import { isCompanyAdminUser } from '@shared/utils/AccessControl';
import {
  addLockedSignupDomain,
  LockedSignupDomain,
  listLockedSignupDomains,
  removeLockedSignupDomain,
} from '@features/settings/services/CompanyService';

/**
 * Lets a company admin list email domains that should not be able to create a brand-new
 * Team Today account. Rows sit as "Pending approval" until Team Today staff approve them.
 * Available on every account type, to Company Admins only.
 */
export function LockedSignupDomainsSetting() {
  const { t } = useTranslation();
  const user = useSelector(selectCurrentUser);
  const isCompanyAdmin = isCompanyAdminUser(user);

  const [domains, setDomains] = useState<LockedSignupDomain[] | undefined>(undefined);
  const [newDomain, setNewDomain] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isCompanyAdmin) return;
    listLockedSignupDomains()
      .then(setDomains)
      .catch(() => setDomains([]));
  }, [isCompanyAdmin]);

  const handleAdd = async () => {
    const value = newDomain.trim();
    if (!value) return;
    setError(null);
    setBusy(true);
    try {
      const created = await addLockedSignupDomain(value);
      setDomains(prev => prev ? [...prev, created] : [created]);
      setNewDomain('');
    } catch (err: any) {
      setError(err?.message || t('settings.locked-signup-domains.add-failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (row: LockedSignupDomain) => {
    if (!window.confirm(t('settings.locked-signup-domains.remove-confirm', { domain: row.domain }))) return;
    setError(null);
    setBusy(true);
    try {
      await removeLockedSignupDomain(row.id);
      setDomains(prev => prev ? prev.filter(d => d.id !== row.id) : prev);
    } catch (err: any) {
      setError(err?.message || t('settings.locked-signup-domains.remove-failed'));
    } finally {
      setBusy(false);
    }
  };

  if (!isCompanyAdmin) {
    return null;
  }

  return (
    <ConfigurationSection className={'lockedSignupDomains__section'}>
      <BodyLarge colour={Colours.blue} style={{ marginBottom: 4 }}>{t('settings.locked-signup-domains.title')}</BodyLarge>
      <BodyRegular style={{ marginBottom: 4 }}>{t('settings.locked-signup-domains.subtitle')}</BodyRegular>
      <BodyRegular style={{ marginBottom: 16, color: Colours.darkGrey }}>{t('settings.locked-signup-domains.approval-note')}</BodyRegular>

      <InlineRow>
        <Input
          type="text"
          maxLength={253}
          placeholder={t('settings.locked-signup-domains.placeholder')}
          value={newDomain}
          onChange={e => setNewDomain(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void handleAdd(); } }}
          disabled={busy}
          data-test="lockedSignupDomainInput"
        />
        <PrimaryButton text={t('settings.locked-signup-domains.add')}
                       size={ButtonSize.Small}
                       click={handleAdd}
                       disabled={busy || !newDomain.trim()} />
      </InlineRow>
      {error && <ErrorBox>{error}</ErrorBox>}

      {domains === undefined ? (
        <SimpleSpinner size={32} />
      ) : domains.length === 0 ? (
        <BodySmall style={{ marginTop: 12, color: Colours.darkGrey }}>{t('settings.locked-signup-domains.empty')}</BodySmall>
      ) : (
        <List>
          {domains.map(row => (
            <RowWrap key={row.id}>
              <RowMain>
                <DomainValue>{row.domain}</DomainValue>
                {row.status === 'APPROVED' && <Pill $variant="ok">{t('settings.locked-signup-domains.status-active')}</Pill>}
                {row.status === 'PENDING' && <Pill $variant="pending">{t('settings.locked-signup-domains.status-pending')}</Pill>}
                {row.status === 'REJECTED' && <Pill $variant="danger">{t('settings.locked-signup-domains.status-rejected')}</Pill>}
              </RowMain>
              {row.status === 'REJECTED' && (
                <BodySmall style={{ color: Colours.darkGrey }}>{t('settings.locked-signup-domains.rejected-help')}</BodySmall>
              )}
              <OutlineButton text={t('settings.locked-signup-domains.remove')}
                             size={ButtonSize.Small}
                             click={() => handleRemove(row)}
                             disabled={busy} />
            </RowWrap>
          ))}
        </List>
      )}
    </ConfigurationSection>
  );
}

const InlineRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const Input = styled.input`
  height: 32px;
  padding: 0 10px;
  border: 1px solid ${Colours.mildGrey};
  border-radius: 4px;
  font-size: 14px;
  min-width: 240px;
  flex: 1;
  max-width: 400px;
`;

const ErrorBox = styled.div`
  margin-top: 12px;
  padding: 8px 12px;
  border: 1px solid ${Colours.red};
  color: ${Colours.red};
  border-radius: 4px;
  font-size: 13px;
`;

const List = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const RowWrap = styled.div`
  padding: 10px 12px;
  border: 1px solid ${Colours.lightGrey};
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  & > button {
    margin-left: auto;
  }
`;

const RowMain = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const DomainValue = styled.span`
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 14px;
  font-weight: 600;
`;

const Pill = styled.span<{ $variant: 'ok' | 'pending' | 'danger' }>`
  font-family: proxima-nova;
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${p => p.$variant === 'ok' ? Colours.green : (p.$variant === 'danger' ? Colours.red : Colours.orange20)};
  color: ${p => p.$variant === 'pending' ? Colours.darkGrey : 'white'};
`;
